import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { createFuelCardApplication, getFuelCardApplicationByOrg, resubmitFuelCardApplication } from '../../../redux/actions/apiActions';
import { HELPLINE_NUMBER } from '../../../utils/constants';
import { panPattern, aadhaarFrontendPattern, vehicleFrontendPattern } from '../../../utils/patterns';
import { s3Upload } from '../../../utils/uploadService';
import { compressImageWithDimension } from '../../../utils/imageCompressor';
import { ObjectId } from 'bson';

const STATUS_COLOR = {
  PENDING: 'warning',
  UNDER_REVIEW: 'info',
  SUBMITTED: 'info',
  ISSUED: 'success',
  REJECTED: 'error',
};

const STATUS_LABEL = {
  PENDING: 'Pending',
  UNDER_REVIEW: 'Under Review',
  SUBMITTED: 'Submitted',
  ISSUED: 'Issued',
  REJECTED: 'Rejected',
};

const STATUS_NOTE = {
  PENDING: { severity: 'warning', text: 'Your application has been received and is in the queue. Our team will begin reviewing it shortly.' },
  UNDER_REVIEW: { severity: 'info', text: 'Our team is currently reviewing your application. No action is needed from your side at this time.' },
  SUBMITTED: { severity: 'info', text: 'Your application has been forwarded to HPCL for processing. You may receive a call for OTP verification — please keep your phone reachable.' },
  ISSUED: { severity: 'success', text: 'Your fuel card has been issued! It will be delivered to your registered address within 10 days. You can start using it upon receipt.' },
  REJECTED: { severity: 'error', text: 'Your application was not approved. Please review the notes below or contact our helpline for further assistance.' },
};

const validationSchema = yup.object({
  contactName: yup.string().trim().required('Contact name is required.'),
  contactPhone: yup
    .string()
    .trim()
    .required('Contact phone is required.')
    .min(10, 'Phone number must be 10 digits.')
    .max(10, 'Phone number must be 10 digits.'),
  contactEmail: yup
    .string()
    .trim()
    .email('Invalid email address.')
    .required('Contact email is required.'),
  aadhaarNumber: yup
    .string()
    .trim()
    .required('Aadhaar number is required.')
    .matches(aadhaarFrontendPattern, 'Invalid Aadhaar number.'),
  panNumber: yup
    .string()
    .trim()
    .required('PAN number is required.')
    .matches(panPattern, 'Invalid PAN format (e.g. ABCDE1234F).'),
  vehicleRCNumber: yup
    .string()
    .trim()
    .required('Vehicle RC number is required.')
    .max(20, 'Vehicle RC number must not exceed 20 characters.')
    .test('vehicle-rc-format', 'Invalid vehicle registration number (e.g. MH12AB1234 or 22BH1234AB).', (value) => {
      if (!value) return true;
      return vehicleFrontendPattern.test(value);
    }),
});

const ImageUploadField = ({ label, preview, onSelect, onRemove, onPreview, inputRef }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      {preview && (
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={preview}
            onClick={() => onPreview(preview)}
            sx={{
              width: 100,
              height: 70,
              objectFit: 'cover',
              borderRadius: 1,
              border: '2px solid',
              borderColor: 'divider',
              cursor: 'pointer',
            }}
          />
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box>
        <input type="file" accept="image/*" hidden ref={inputRef} onChange={onSelect} />
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          size="small"
        >
          {preview ? 'Change' : 'Upload'}
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
          Max: 5MB (JPEG, PNG, WebP)
        </Typography>
      </Box>
    </Box>
  </Box>
);

const FuelCardApplication = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.authReducer.user);
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const [openSidebar, setOpenSidebar] = useState(false);
  const open = isMd ? false : openSidebar;

  const [insuranceOpen, setInsuranceOpen] = useState(false);
  const [roadsideOpen, setRoadsideOpen] = useState(false);

  // Existing application state
  const [existingApplication, setExistingApplication] = useState(null);
  const [fetchingApplication, setFetchingApplication] = useState(true);
  const [showReapplyForm, setShowReapplyForm] = useState(false);

  // Aadhaar: 2 images (front, back)
  const [aadhaarFiles, setAadhaarFiles] = useState([null, null]);
  const [aadhaarPreviews, setAadhaarPreviews] = useState([null, null]);

  // PAN: 1 image
  const [panFile, setPanFile] = useState(null);
  const [panPreview, setPanPreview] = useState(null);

  // Vehicle RC: 2 images (front, back)
  const [vehicleRCFiles, setVehicleRCFiles] = useState([null, null]);
  const [vehicleRCPreviews, setVehicleRCPreviews] = useState([null, null]);

  const aadhaarFrontInputRef = useRef(null);
  const aadhaarBackInputRef = useRef(null);
  const panInputRef = useRef(null);
  const vehicleRCFrontInputRef = useRef(null);
  const vehicleRCBackInputRef = useRef(null);

  // Image preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    if (!user?._id) return;
    async function checkExistingApplication() {
      try {
        const res = await getFuelCardApplicationByOrg(user._id);
        if (res?.data?.data) {
          setExistingApplication(res.data.data);
        }
      } catch (error) {
        // 404 means no application yet — that's fine
        if (error.response?.status !== 404) {
          toast.error(error.response?.data?.msg ?? 'Error checking existing application');
        }
      } finally {
        setFetchingApplication(false);
      }
    }
    checkExistingApplication();
  }, [user?._id]);

  // Pre-fill image previews from existing data when status is REJECTED
  useEffect(() => {
    if (existingApplication?.applicationStatus === 'REJECTED') {
      setAadhaarPreviews([
        existingApplication.aadhaar?.imageUrls?.[0] || null,
        existingApplication.aadhaar?.imageUrls?.[1] || null,
      ]);
      setPanPreview(existingApplication.pan?.imageUrls?.[0] || null);
      setVehicleRCPreviews([
        existingApplication.vehicleRC?.imageUrls?.[0] || null,
        existingApplication.vehicleRC?.imageUrls?.[1] || null,
      ]);
    }
  }, [existingApplication]);

  const handleResubmit = async (values) => {
    // Use existing URLs as fallback if no new file was selected
    const aadhaarUrls = [
      existingApplication.aadhaar?.imageUrls?.[0] || null,
      existingApplication.aadhaar?.imageUrls?.[1] || null,
    ];
    let panUrl = existingApplication.pan?.imageUrls?.[0] || null;
    const vehicleRCUrls = [
      existingApplication.vehicleRC?.imageUrls?.[0] || null,
      existingApplication.vehicleRC?.imageUrls?.[1] || null,
    ];

    dispatch(setGlobalLoader(true));

    try {
      // Upload new aadhaar images if provided
      for (let i = 0; i < 2; i++) {
        if (aadhaarFiles[i]) {
          const compressed = await compressImageWithDimension(aadhaarFiles[i], 1200, 0.9);
          const result = await s3Upload(compressed, { entity: 'organization', entityId: existingApplication._id });
          if (result?.[0]?.url) aadhaarUrls[i] = result[0].url;
        }
      }

      if (!aadhaarUrls[0] || !aadhaarUrls[1]) {
        dispatch(setGlobalLoader(false));
        toast.error('Please upload both Aadhaar front and back images.');
        return;
      }

      // Upload new PAN image if provided
      if (panFile) {
        const compressedPan = await compressImageWithDimension(panFile, 1200, 0.9);
        const panResult = await s3Upload(compressedPan, { entity: 'organization', entityId: existingApplication._id });
        if (panResult?.[0]?.url) panUrl = panResult[0].url;
      }

      if (!panUrl) {
        dispatch(setGlobalLoader(false));
        toast.error('Please upload your PAN image.');
        return;
      }

      // Upload new vehicle RC images if provided
      for (let i = 0; i < 2; i++) {
        if (vehicleRCFiles[i]) {
          const compressed = await compressImageWithDimension(vehicleRCFiles[i], 1200, 0.9);
          const result = await s3Upload(compressed, { entity: 'organization', entityId: existingApplication._id });
          if (result?.[0]?.url) vehicleRCUrls[i] = result[0].url;
        }
      }

      if (!vehicleRCUrls[0] || !vehicleRCUrls[1]) {
        dispatch(setGlobalLoader(false));
        toast.error('Please upload both Vehicle RC front and back images.');
        return;
      }

      await resubmitFuelCardApplication({
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail,
        aadhaar: { number: values.aadhaarNumber, imageUrls: aadhaarUrls },
        pan: { number: values.panNumber.toUpperCase(), imageUrls: [panUrl] },
        vehicleRC: { number: values.vehicleRCNumber || null, imageUrls: vehicleRCUrls },
      });

      toast.success('Fuel card application resubmitted successfully');
      window.location.reload();
    } catch (error) {
      console.log('resubmit fuel card error', error);
      toast.error(error.response?.data?.msg ?? 'Error occurred while resubmitting application');
    } finally {
      dispatch(setGlobalLoader(false));
    }
  };

  const handleImageSelect = (event, type, index = 0) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === 'aadhaar') {
      const newFiles = [...aadhaarFiles];
      const newPreviews = [...aadhaarPreviews];
      newFiles[index] = file;
      newPreviews[index] = previewUrl;
      setAadhaarFiles(newFiles);
      setAadhaarPreviews(newPreviews);
    } else if (type === 'pan') {
      setPanFile(file);
      setPanPreview(previewUrl);
    } else if (type === 'vehicleRC') {
      const newFiles = [...vehicleRCFiles];
      const newPreviews = [...vehicleRCPreviews];
      newFiles[index] = file;
      newPreviews[index] = previewUrl;
      setVehicleRCFiles(newFiles);
      setVehicleRCPreviews(newPreviews);
    }
  };

  const handleRemoveImage = (type, index = 0) => {
    if (type === 'aadhaar') {
      const newFiles = [...aadhaarFiles];
      const newPreviews = [...aadhaarPreviews];
      newFiles[index] = null;
      newPreviews[index] = null;
      setAadhaarFiles(newFiles);
      setAadhaarPreviews(newPreviews);
    } else if (type === 'pan') {
      setPanFile(null);
      setPanPreview(null);
    } else if (type === 'vehicleRC') {
      const newFiles = [...vehicleRCFiles];
      const newPreviews = [...vehicleRCPreviews];
      newFiles[index] = null;
      newPreviews[index] = null;
      setVehicleRCFiles(newFiles);
      setVehicleRCPreviews(newPreviews);
    }
  };

  const handleOnSubmit = async (values) => {
    if (!aadhaarFiles[0] || !aadhaarFiles[1]) {
      toast.error('Please upload both Aadhaar front and back images.');
      return;
    }
    if (!panFile) {
      toast.error('Please upload your PAN image.');
      return;
    }
    if (!vehicleRCFiles[0] || !vehicleRCFiles[1]) {
      toast.error('Please upload both Vehicle RC front and back images.');
      return;
    }

    dispatch(setGlobalLoader(true));

    const entityId = new ObjectId().toString();

    try {
      const payload = {
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail,
        aadhaar: { number: values.aadhaarNumber, imageUrls: [] },
        pan: { number: values.panNumber.toUpperCase(), imageUrls: [] },
        vehicleRC: { number: values.vehicleRCNumber || null, imageUrls: [] },
      };

      // Upload aadhaar images (front + back)
      for (const file of aadhaarFiles) {
        if (file) {
          const compressed = await compressImageWithDimension(file, 1200, 0.9);
          const result = await s3Upload(compressed, { entity: 'organization', entityId });
          if (result && result[0]?.url) payload.aadhaar.imageUrls.push(result[0].url);
        }
      }

      // Upload PAN image (1)
      const compressedPan = await compressImageWithDimension(panFile, 1200, 0.9);
      const panResult = await s3Upload(compressedPan, { entity: 'organization', entityId });
      if (panResult && panResult[0]?.url) payload.pan.imageUrls.push(panResult[0].url);

      // Upload vehicle RC images (front + back)
      for (const file of vehicleRCFiles) {
        if (file) {
          const compressed = await compressImageWithDimension(file, 1200, 0.9);
          const result = await s3Upload(compressed, { entity: 'organization', entityId });
          if (result && result[0]?.url) payload.vehicleRC.imageUrls.push(result[0].url);
        }
      }

      await createFuelCardApplication(payload);
      toast.success('Fuel card application submitted successfully');
      window.location.reload();
    } catch (error) {
      console.log('fuel card application error', error);
      toast.error(error.response?.data?.msg ?? 'Error occurred while submitting application');
    } finally {
      dispatch(setGlobalLoader(false));
    }
  };

  const statusColor = STATUS_COLOR[existingApplication?.applicationStatus] || 'default';

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={() => setOpenSidebar(false)}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <br />
        <br />

        <Box overflow="hidden" marginLeft={{ md: '270px' }} paddingX={{ xs: 2, md: 4 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }} fontWeight={700}>
            Fuel Card Application
          </Typography>

          {/* Alerts row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Alert severity="success" sx={{ height: '100%' }}>
                <strong>1% cashback every month</strong> on all your fuel spends with the HPCL Fuel Card. Accepted at HPCL fuel stations nationwide.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Alert severity="warning" sx={{ height: '100%' }}>
                You will receive a call from our fuel provider <strong>(HPCL)</strong> to get an OTP for registration confirmation. Please keep your phone reachable.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Alert severity="info" sx={{ height: '100%' }}>
                For any clarification, please call our helpline:&nbsp;
                <strong>
                  <a href={`tel:${HELPLINE_NUMBER}`} style={{ color: 'inherit' }}>
                    {HELPLINE_NUMBER}
                  </a>
                </strong>
              </Alert>
            </Grid>
          </Grid>

          {/* Free Insurance Benefits — collapsible */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box
              onClick={() => setInsuranceOpen(p => !p)}
              sx={{
                bgcolor: '#7B1F1F',
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="white">
                Free Insurance* for Drivers and Helpers
              </Typography>
              {insuranceOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
            </Box>
            <Collapse in={insuranceOpen}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.5}>
                  {[
                    { amount: '₹5 Lakh', label: 'Accidental Death Insurance', color: '#c0392b' },
                    { amount: '₹3 Lakh', label: 'Permanent Disability Caused Due To Accident', color: '#e67e22' },
                    { amount: '₹1 Lakh', label: 'Partial Permanent Disability Caused Due To Accident', color: '#2c3e50' },
                    { amount: '₹40,000', label: 'Reimbursement of Medical Bills Caused Due To Accident', color: '#27ae60' },
                    { amount: '₹12,000', label: 'For Education Of Children (One Time)', color: '#2980b9' },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          p: 1.5,
                          height: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: item.color,
                            color: 'white',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            minWidth: 72,
                            textAlign: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                            {item.amount}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  * Terms and conditions apply. Insurance benefits are subject to HPCL fuel card policy.
                </Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Free Roadside Assistance — collapsible */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 4 }}>
            <Box
              onClick={() => setRoadsideOpen(p => !p)}
              sx={{
                bgcolor: '#7B1F1F',
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="white">
                Free Roadside Assistance
              </Typography>
              {roadsideOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
            </Box>
            <Collapse in={roadsideOpen}>
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Toll Free: <strong>18002088800</strong>
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    { badge: '24x7', label: 'Roadside Assistance Pan India', color: '#c0392b' },
                    { badge: '14000+', label: 'Trained Mechanics Available on Call', color: '#2c3e50' },
                    { badge: 'Free Towing', label: 'Up to 50 KMS or to Nearest Service Point', color: '#e67e22' },
                    { badge: '4 hrs', label: 'Maximum Response Time', color: '#27ae60' },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          p: 1.5,
                          height: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: item.color,
                            color: 'white',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            minWidth: 72,
                            textAlign: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                            {item.badge}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Collapse>
          </Box>

          {fetchingApplication ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Checking application status...</Typography>
            </Box>
          ) : existingApplication ? (
            /* ── Existing Application Status Card + Reapply Form ── */
            <>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                maxWidth: 600,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Application Status
                  </Typography>
                  <Chip
                    label={STATUS_LABEL[existingApplication.applicationStatus] ?? existingApplication.applicationStatus}
                    color={statusColor}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
                {existingApplication.applicationStatus === 'REJECTED' && !showReapplyForm && (
                  <Button variant="contained" color="primary" size="small" onClick={() => setShowReapplyForm(true)}>
                    Reapply
                  </Button>
                )}
              </Box>
              {STATUS_NOTE[existingApplication.applicationStatus] && (
                <Alert
                  severity={STATUS_NOTE[existingApplication.applicationStatus].severity}
                  sx={{ mb: 3 }}
                >
                  {STATUS_NOTE[existingApplication.applicationStatus].text}
                  
                  {existingApplication.applicationStatus === 'REJECTED' && existingApplication.reviewNotes && (
                    <> <br/><strong> Reason- {existingApplication.reviewNotes}</strong></>
                  )}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact Name</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.contactName || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.contactPhone || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Contact Email</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.contactEmail || '—'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Aadhaar Number</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.aadhaar?.number || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">PAN Number</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.pan?.number || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Vehicle RC Number</Typography>
                  <Typography variant="body2" fontWeight={600}>{existingApplication.vehicleRC?.number || '—'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Submitted On</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatDate(existingApplication.createdAt)}</Typography>
                </Grid>

              </Grid>
            </Box>

            {/* ── Reapply Form ── */}
            {showReapplyForm && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Resubmit Application</Typography>
                <Button variant="text" size="small" onClick={() => setShowReapplyForm(false)}>Cancel</Button>
              </Box>
              <Formik
                initialValues={{
                  contactName: existingApplication.contactName || '',
                  contactPhone: existingApplication.contactPhone || '',
                  contactEmail: existingApplication.contactEmail || '',
                  aadhaarNumber: existingApplication.aadhaar?.number || '',
                  panNumber: existingApplication.pan?.number || '',
                  vehicleRCNumber: existingApplication.vehicleRC?.number || '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleResubmit}
                enableReinitialize
              >
                {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
                  <form onSubmit={handleSubmit}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Contact Information</Typography>
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>Contact Name *</Typography>
                        <TextField placeholder="Enter contact name" variant="outlined" fullWidth size="small"
                          value={values.contactName} onChange={(e) => setFieldValue('contactName', e.target.value)}
                          error={touched.contactName && Boolean(errors.contactName)} helperText={touched.contactName && errors.contactName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>Contact Phone *</Typography>
                        <TextField placeholder="Enter 10-digit phone number" variant="outlined" fullWidth size="small" type="number"
                          value={values.contactPhone}
                          onChange={(e) => e.target.value.toString().length > 10 ? setFieldValue('contactPhone', e.target.value.substring(0, 10)) : setFieldValue('contactPhone', e.target.value)}
                          error={touched.contactPhone && Boolean(errors.contactPhone)} helperText={touched.contactPhone && errors.contactPhone}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>Contact Email *</Typography>
                        <TextField placeholder="Enter email address" variant="outlined" fullWidth size="small" type="email"
                          value={values.contactEmail} onChange={(e) => setFieldValue('contactEmail', e.target.value)}
                          error={touched.contactEmail && Boolean(errors.contactEmail)} helperText={touched.contactEmail && errors.contactEmail}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 3 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Aadhaar Details</Typography>
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>Aadhaar Number *</Typography>
                        <TextField placeholder="Enter 12-digit Aadhaar number" variant="outlined" fullWidth size="small" type="number"
                          value={values.aadhaarNumber}
                          onChange={(e) => { const val = e.target.value.toString(); if (val.length <= 12) setFieldValue('aadhaarNumber', val); }}
                          error={touched.aadhaarNumber && Boolean(errors.aadhaarNumber)} helperText={touched.aadhaarNumber && errors.aadhaarNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ImageUploadField label="Aadhaar Front *" preview={aadhaarPreviews[0]}
                          onSelect={(e) => handleImageSelect(e, 'aadhaar', 0)} onRemove={() => handleRemoveImage('aadhaar', 0)}
                          onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }} inputRef={aadhaarFrontInputRef}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ImageUploadField label="Aadhaar Back *" preview={aadhaarPreviews[1]}
                          onSelect={(e) => handleImageSelect(e, 'aadhaar', 1)} onRemove={() => handleRemoveImage('aadhaar', 1)}
                          onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }} inputRef={aadhaarBackInputRef}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 3 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>PAN Details</Typography>
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>PAN Number *</Typography>
                        <TextField placeholder="e.g. ABCDE1234F" variant="outlined" fullWidth size="small"
                          value={values.panNumber} onChange={(e) => setFieldValue('panNumber', e.target.value.toUpperCase())}
                          inputProps={{ maxLength: 10 }}
                          error={touched.panNumber && Boolean(errors.panNumber)} helperText={touched.panNumber && errors.panNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ImageUploadField label="PAN Image *" preview={panPreview}
                          onSelect={(e) => handleImageSelect(e, 'pan')} onRemove={() => handleRemoveImage('pan')}
                          onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }} inputRef={panInputRef}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 3 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Vehicle RC Details</Typography>
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>Vehicle RC Number</Typography>
                        <TextField placeholder="Enter vehicle RC number" variant="outlined" fullWidth size="small"
                          value={values.vehicleRCNumber} inputProps={{ maxLength: 20 }}
                          onChange={(e) => setFieldValue('vehicleRCNumber', e.target.value.toUpperCase())}
                          error={touched.vehicleRCNumber && Boolean(errors.vehicleRCNumber)} helperText={touched.vehicleRCNumber && errors.vehicleRCNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ImageUploadField label="Vehicle RC Front *" preview={vehicleRCPreviews[0]}
                          onSelect={(e) => handleImageSelect(e, 'vehicleRC', 0)} onRemove={() => handleRemoveImage('vehicleRC', 0)}
                          onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }} inputRef={vehicleRCFrontInputRef}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ImageUploadField label="Vehicle RC Back *" preview={vehicleRCPreviews[1]}
                          onSelect={(e) => handleImageSelect(e, 'vehicleRC', 1)} onRemove={() => handleRemoveImage('vehicleRC', 1)}
                          onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }} inputRef={vehicleRCBackInputRef}
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, mb: 4, display: 'flex', gap: 2 }}>
                      <Button variant="contained" type="submit" disabled={isSubmitting} size="large">
                        {isSubmitting ? <CircularProgress size={24} /> : 'Reapply'}
                      </Button>
                      <Button variant="outlined" size="large" onClick={() => setShowReapplyForm(false)} disabled={isSubmitting}>
                        Cancel
                      </Button>
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
            )}
            </>
          ) : (
            /* ── Application Form ── */
            <Formik
              initialValues={{
                contactName: '',
                contactPhone: '',
                contactEmail: '',
                aadhaarNumber: '',
                panNumber: '',
                vehicleRCNumber: '',
              }}
              validationSchema={validationSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>

                  {/* Contact Information */}
                  <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                    Contact Information
                  </Typography>
                  <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        Contact Name *
                      </Typography>
                      <TextField
                        placeholder="Enter contact name"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={values.contactName}
                        onChange={(e) => setFieldValue('contactName', e.target.value)}
                        error={touched.contactName && Boolean(errors.contactName)}
                        helperText={touched.contactName && errors.contactName}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        Contact Phone *
                      </Typography>
                      <TextField
                        placeholder="Enter 10-digit phone number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        value={values.contactPhone}
                        onChange={(e) =>
                          e.target.value.toString().length > 10
                            ? setFieldValue('contactPhone', e.target.value.substring(0, 10))
                            : setFieldValue('contactPhone', e.target.value)
                        }
                        error={touched.contactPhone && Boolean(errors.contactPhone)}
                        helperText={touched.contactPhone && errors.contactPhone}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        Contact Email *
                      </Typography>
                      <TextField
                        placeholder="Enter email address"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="email"
                        value={values.contactEmail}
                        onChange={(e) => setFieldValue('contactEmail', e.target.value)}
                        error={touched.contactEmail && Boolean(errors.contactEmail)}
                        helperText={touched.contactEmail && errors.contactEmail}
                      />
                    </Grid>
                  </Grid>

                  {/* Aadhaar */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                    Aadhaar Details
                  </Typography>
                  <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        Aadhaar Number *
                      </Typography>
                      <TextField
                        placeholder="Enter 12-digit Aadhaar number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        value={values.aadhaarNumber}
                        onChange={(e) => {
                          const val = e.target.value.toString();
                          if (val.length <= 12) setFieldValue('aadhaarNumber', val);
                        }}
                        error={touched.aadhaarNumber && Boolean(errors.aadhaarNumber)}
                        helperText={touched.aadhaarNumber && errors.aadhaarNumber}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadField
                        label="Aadhaar Front *"
                        preview={aadhaarPreviews[0]}
                        onSelect={(e) => handleImageSelect(e, 'aadhaar', 0)}
                        onRemove={() => handleRemoveImage('aadhaar', 0)}
                        onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }}
                        inputRef={aadhaarFrontInputRef}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadField
                        label="Aadhaar Back *"
                        preview={aadhaarPreviews[1]}
                        onSelect={(e) => handleImageSelect(e, 'aadhaar', 1)}
                        onRemove={() => handleRemoveImage('aadhaar', 1)}
                        onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }}
                        inputRef={aadhaarBackInputRef}
                      />
                    </Grid>
                  </Grid>

                  {/* PAN */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                    PAN Details
                  </Typography>
                  <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        PAN Number *
                      </Typography>
                      <TextField
                        placeholder="e.g. ABCDE1234F"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={values.panNumber}
                        onChange={(e) => setFieldValue('panNumber', e.target.value.toUpperCase())}
                        inputProps={{ maxLength: 10 }}
                        error={touched.panNumber && Boolean(errors.panNumber)}
                        helperText={touched.panNumber && errors.panNumber}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadField
                        label="PAN Image *"
                        preview={panPreview}
                        onSelect={(e) => handleImageSelect(e, 'pan')}
                        onRemove={() => handleRemoveImage('pan')}
                        onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }}
                        inputRef={panInputRef}
                      />
                    </Grid>
                  </Grid>

                  {/* Vehicle RC */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                    Vehicle RC Details
                  </Typography>
                  <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" sx={{ marginBottom: 1 }} fontWeight={700}>
                        Vehicle RC Number
                      </Typography>
                      <TextField
                        placeholder="Enter vehicle RC number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={values.vehicleRCNumber}
                        inputProps={{ maxLength: 20 }}
                        onChange={(e) => setFieldValue('vehicleRCNumber', e.target.value.toUpperCase())}
                        error={touched.vehicleRCNumber && Boolean(errors.vehicleRCNumber)}
                        helperText={touched.vehicleRCNumber && errors.vehicleRCNumber}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadField
                        label="Vehicle RC Front *"
                        preview={vehicleRCPreviews[0]}
                        onSelect={(e) => handleImageSelect(e, 'vehicleRC', 0)}
                        onRemove={() => handleRemoveImage('vehicleRC', 0)}
                        onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }}
                        inputRef={vehicleRCFrontInputRef}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadField
                        label="Vehicle RC Back *"
                        preview={vehicleRCPreviews[1]}
                        onSelect={(e) => handleImageSelect(e, 'vehicleRC', 1)}
                        onRemove={() => handleRemoveImage('vehicleRC', 1)}
                        onPreview={(url) => { setPreviewImage(url); setPreviewOpen(true); }}
                        inputRef={vehicleRCBackInputRef}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4, mb: 4 }}>
                    <Button variant="contained" type="submit" disabled={isSubmitting} size="large">
                      {isSubmitting ? <CircularProgress size={24} /> : 'Submit Application'}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          )}
        </Box>
      </main>

      {/* Image Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={previewImage}
            sx={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FuelCardApplication;
